const CUSTOMIZER_MESSAGE_TYPE = "vans-customs";
const INTERNAL_MESSAGE_TYPE = "customs_tools";
const ALLOWED_MESSAGE_COMMANDS = [
  "addToCart",
  "parentAddToCart",
  "addToFavorites",
  "parentAddToFavorites",
  "customsAddToCartSignInToBuy"
];
const SAFE_URL_PROTOCOLS = ["http:", "https:"];
const LOOPBACK_HOSTNAMES = ["localhost", "127.0.0.1", "[::1]", "::1"];
const PRODUCTION_ENVIRONMENTS = ["prod", "production"];

const normalizeOrigin = (value) => {
  if (!value || typeof value !== "string" || value === "null") return null;

  try {
    const valueWithProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
    return new URL(valueWithProtocol).origin;
  } catch (e) {
    return null;
  }
};

const getWindowOrigin = () => {
  if (typeof window === "undefined" || !window.location || !window.location.origin) return null;
  return window.location.origin;
};

const getWindowHref = () => {
  if (typeof window === "undefined" || !window.location || !window.location.href) return null;
  return window.location.href;
};

const getAppEnvironment = () => {
  return (process.env.VUE_APP_ENV || process.env["NODE_ENV"] || "").toLowerCase();
};

const isLowerEnvironment = () => {
  const appEnvironment = getAppEnvironment();
  return !!appEnvironment && PRODUCTION_ENVIRONMENTS.indexOf(appEnvironment) === -1;
};

const isLoopbackOrigin = (origin) => {
  const normalizedOrigin = normalizeOrigin(origin);
  if (!normalizedOrigin) return false;

  try {
    const { hostname } = new URL(normalizedOrigin);
    return LOOPBACK_HOSTNAMES.indexOf(hostname) !== -1;
  } catch (e) {
    return false;
  }
};

const isPlainObject = (value) => {
  return value !== null && typeof value === "object" && !Array.isArray(value);
};

const getCommandForLog = (data) => {
  return isPlainObject(data) && typeof data.command === "string" ? data.command : undefined;
};

const logRejectedPostMessage = ({ origin, command, reason }) => {
  console.warn("customizerMessagingSecurity: rejected postMessage", {
    origin,
    command,
    reason
  });
};

const logRejectedRedirect = ({ context, reason }) => {
  console.warn("customizerMessagingSecurity: rejected redirectUrl", {
    context,
    reason
  });
};

// postMessage is a cross-origin boundary; production is exact same-origin only, with loopback allowed for lower-env local testing.
const isTrustedCustomizerOrigin = (origin) => {
  const normalizedOrigin = normalizeOrigin(origin);
  if (!normalizedOrigin) return false;
  if (normalizedOrigin === normalizeOrigin(getWindowOrigin())) return true;
  return isLowerEnvironment() && isLoopbackOrigin(normalizedOrigin);
};

// The toolkit only processes known customizer commands with an object payload.
const validateCustomizerMessageEvent = (event) => {
  const data = event && event.data;
  const origin = event && event.origin;
  const command = getCommandForLog(data);

  if (!isTrustedCustomizerOrigin(origin)) {
    return { valid: false, reason: "untrusted-origin", origin, command };
  }

  if (!isPlainObject(data)) {
    return { valid: false, reason: "invalid-data", origin, command };
  }

  if (data.type === INTERNAL_MESSAGE_TYPE) {
    return { valid: false, reason: "internal-message-type", origin, command };
  }

  if (data.type !== CUSTOMIZER_MESSAGE_TYPE) {
    return { valid: false, reason: "unexpected-message-type", origin, command };
  }

  if (ALLOWED_MESSAGE_COMMANDS.indexOf(data.command) === -1) {
    return { valid: false, reason: "unexpected-command", origin, command };
  }

  if (!isPlainObject(data.body)) {
    return { valid: false, reason: "invalid-body", origin, command };
  }

  return {
    valid: true,
    message: {
      origin,
      source: event.source,
      type: data.type,
      command: data.command,
      body: data.body
    }
  };
};

const isProtocolRelativeUrl = (url) => /^\/\//.test(url);
const hasControlCharacters = (url) => /[\u0000-\u001F\u007F]/.test(url);

const getSafeRedirectUrl = (redirectUrl, options = {}) => {
  if (!redirectUrl || typeof redirectUrl !== "string") {
    return null;
  }

  const trimmedRedirectUrl = redirectUrl.trim();
  const baseUrl = options.baseUrl || getWindowHref();

  if (!trimmedRedirectUrl || !baseUrl || isProtocolRelativeUrl(trimmedRedirectUrl) || hasControlCharacters(trimmedRedirectUrl)) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedRedirectUrl, baseUrl);
    if (SAFE_URL_PROTOCOLS.indexOf(parsedUrl.protocol) === -1) {
      return null;
    }

    const baseOrigin = new URL(baseUrl).origin;
    if (parsedUrl.origin === baseOrigin) {
      return parsedUrl.href;
    }
  } catch (e) {
    return null;
  }

  return null;
};

// Redirect URLs may be attacker-controlled in postMessage/API responses, so navigate only after URL parsing.
const navigateToSafeRedirectUrl = (redirectUrl, options = {}) => {
  const safeRedirectUrl = getSafeRedirectUrl(redirectUrl, options);

  if (!safeRedirectUrl) {
    logRejectedRedirect({
      context: options.context,
      reason: "unsafe-url"
    });
    return false;
  }

  window.location.href = safeRedirectUrl;
  return true;
};

export {
  CUSTOMIZER_MESSAGE_TYPE,
  INTERNAL_MESSAGE_TYPE,
  ALLOWED_MESSAGE_COMMANDS,
  getSafeRedirectUrl,
  isTrustedCustomizerOrigin,
  logRejectedPostMessage,
  navigateToSafeRedirectUrl,
  normalizeOrigin,
  validateCustomizerMessageEvent
};
